import React from 'react';
import { MakeDepositResponse } from '../../../../models/bmp.model';
import { formatCurrency } from '../../utils/formatCurrency';

interface Props {
    data: MakeDepositResponse | null;
    onClose: () => void;
}

export const DepositSuccessModal: React.FC<Props> = ({ data, onClose }) => {
    if (!data) return null;

    const { breakdown } = data;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-6 bg-slate-900/80 backdrop-blur-md">
            <div className="bg-white w-full max-w-2xl rounded-[2rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 flex flex-col max-h-[95vh]">
                
                {/* Header - Kisebb, de elegánsabb, hogy maradjon hely az összegeknek */}
                <div className="bg-emerald-500 p-6 text-center relative overflow-hidden shrink-0">
                    <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 shadow-inner">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-black text-white tracking-tight">Sikeres Befektetés!</h2>
                    <p className="text-emerald-50 text-xs font-bold uppercase tracking-widest mt-1 opacity-90">
                        Csoport ID: #{data.depositGroupId}
                    </p>
                </div>

                {/* Görgess tartalom - Ha kisebb a kijelző, lehessen görgetni */}
                <div className="p-6 md:p-8 space-y-5 overflow-y-auto custom-scrollbar">
                    
                    {/* TRADING Zseb - Legnagyobb hangsúly (60%) */}
                    <div className="bg-slate-900 rounded-3xl p-5 md:p-6 shadow-lg border border-slate-800">
                        <h4 className="text-xs font-black text-amber-400 uppercase tracking-widest mb-4">Trading Portfólió (60%)</h4>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-slate-400 font-bold uppercase tracking-wider text-sm">Bitcoin</span>
                                <span className="text-2xl md:text-3xl font-black text-white">{formatCurrency(Number(breakdown.trading.btcUsd))}</span>
                            </div>
                            <div className="flex justify-between items-center pt-4 border-t border-slate-800">
                                <span className="text-slate-400 font-bold uppercase tracking-wider text-sm">Ethereum</span>
                                <span className="text-2xl md:text-3xl font-black text-white">{formatCurrency(Number(breakdown.trading.ethUsd))}</span>
                            </div>
                            <div className="flex justify-between items-center pt-4 border-t border-slate-800">
                                <span className="text-amber-500/80 font-bold uppercase tracking-wider text-sm">Likviditás</span>
                                <span className="text-2xl md:text-3xl font-black text-amber-400">{formatCurrency(Number(breakdown.trading.liquidityUsd))}</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* FIX Zseb */}
                        <div className="bg-amber-50/50 rounded-3xl p-5 border border-amber-200/60">
                            <h4 className="text-xs font-black text-amber-600 uppercase tracking-widest mb-4">Fix Portfólió (20%)</h4>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">BTC</p>
                                        <p className="text-xs font-mono text-slate-400 mt-0.5">{Number(breakdown.fix.btcAmt).toFixed(8)}</p>
                                    </div>
                                    <p className="text-xl font-black text-slate-800">{formatCurrency(Number(breakdown.fix.btcUsd))}</p>
                                </div>
                                <div className="flex justify-between items-center pt-4 border-t border-amber-200/60">
                                    <div>
                                        <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">ETH</p>
                                        <p className="text-xs font-mono text-slate-400 mt-0.5">{Number(breakdown.fix.ethAmt).toFixed(8)}</p>
                                    </div>
                                    <p className="text-xl font-black text-slate-800">{formatCurrency(Number(breakdown.fix.ethUsd))}</p>
                                </div>
                            </div>
                        </div>

                        {/* ROBOT Zseb */}
                        <div className="bg-slate-50 rounded-3xl p-5 border border-slate-200">
                            <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4">Robot Portfólió (20%)</h4>
                            <div className="space-y-4 flex flex-col h-full justify-between">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">Likviditás</span>
                                    <span className="text-xl font-black text-slate-800">{formatCurrency(Number(breakdown.robot.liquidityUsd))}</span>
                                </div>
                                <div className="flex justify-between items-center pt-4 border-t border-slate-200 opacity-50">
                                    <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">BTC</span>
                                    <span className="text-lg font-bold text-slate-500">$0.00</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Meta adatok */}
                    <div className="bg-slate-100/50 rounded-2xl p-4 flex justify-between items-center border border-slate-200 mt-2">
                        <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Egységár</p>
                            <p className="text-base font-black text-slate-700">{Number(data.appliedRate).toFixed(4)} <span className="text-xs text-slate-500 font-bold">USD/Unit</span></p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Résztvevők</p>
                            <p className="text-base font-black text-slate-700">{data.participantsCount} <span className="text-xs text-slate-500 font-bold">fő</span></p>
                        </div>
                    </div>
                </div>

                {/* Gomb */}
                <div className="p-6 pt-2 shrink-0 bg-white">
                    <button 
                        onClick={onClose}
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white text-lg font-black py-4 rounded-2xl transition-all shadow-xl hover:shadow-2xl active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                        Tovább a portfólióhoz
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};